const DEG2RAD = Math.PI / 180;

/**
 * Returns true when the sun is above the horizon for a given time and location.
 * Uses a low-precision solar position algorithm (accurate to ~1°).
 */
export function isSunAboveHorizon(date: Date, lat: number, lon: number): boolean {
  const JD = date.getTime() / 86400000 + 2440587.5;
  const n = JD - 2451545.0; // days since J2000.0

  // Mean longitude & mean anomaly (degrees)
  const L = ((280.460 + 0.9856474 * n) % 360 + 360) % 360;
  const g = ((357.528 + 0.9856003 * n) % 360 + 360) % 360 * DEG2RAD;

  // Ecliptic longitude (radians)
  const lambda = (L + 1.915 * Math.sin(g) + 0.020 * Math.sin(2 * g)) * DEG2RAD;

  // Obliquity of the ecliptic
  const epsilon = 23.439 * DEG2RAD;

  // Solar declination
  const dec = Math.asin(Math.sin(epsilon) * Math.sin(lambda));

  // Right ascension (radians)
  const RA = Math.atan2(Math.cos(epsilon) * Math.sin(lambda), Math.cos(lambda));

  // Greenwich Mean Sidereal Time (degrees)
  const GMST = ((280.46061837 + 360.98564736629 * n) % 360 + 360) % 360;

  // Hour angle (radians)
  const HA = (GMST + lon - RA / DEG2RAD) * DEG2RAD;

  // Solar altitude
  const latRad = lat * DEG2RAD;
  const altitude = Math.asin(
    Math.sin(latRad) * Math.sin(dec) +
    Math.cos(latRad) * Math.cos(dec) * Math.cos(HA),
  );

  return altitude > 0;
}

/**
 * Compute daytime from the midpoint of a pass and a geographic location.
 */
export function computeIsDaytime(startMs: number, endMs: number, lat: number, lon: number): boolean {
  const midMs = (startMs + endMs) / 2;
  return isSunAboveHorizon(new Date(midMs), lat, lon);
}
